import { localStorageService } from '@wf/sw-ui-kit';
import { type IEachItem } from '@wf/components';

import { type ITableColumn } from '../../../../components/TableGrid';
import LicenseStatus from '../../../../components/LicenseStatus';
import DateTimeCell from '../../../../components/DateTimeCell';
import CompanyCell from '../../../../components/CompanyCell';

import { type ILicenseSearchResult } from '../../store/types';
import { LPCO_TABLE_NAME } from '../../constants';

import { formatDate } from '../../../../helpers/dateHelpers';
import { getColumnSettings } from './helpers';

import styles from '../LicensesTable/LicensesTable.module.scss';

export const licenseTableKeys = {
  id: 'id',
  status: 'status',
  licenseType: 'licenseType',
  userReferenceNumber: 'userReferenceNumber',
  approvalReference: 'approvalReference',
  approvalDate: 'approvalDate',
  companyCode: 'companyCode',
  companyDescription: 'companyDescription',
  lastOperationDate: 'lastOperationDate',
  requestNumber: 'requestNumber',
  requestDate: 'requestDate',
};

const defaultCheckStatuses = {
  [LPCO_TABLE_NAME.NEW_REQUESTS]: {
    [licenseTableKeys.status]: true,
    [licenseTableKeys.licenseType]: true,
    [licenseTableKeys.userReferenceNumber]: true,
    [licenseTableKeys.requestNumber]: true,
    [licenseTableKeys.requestDate]: true,
    [licenseTableKeys.companyCode]: true,
    [licenseTableKeys.lastOperationDate]: true,
  },
  [LPCO_TABLE_NAME.LIST]: {
    [licenseTableKeys.status]: true,
    [licenseTableKeys.licenseType]: true,
    [licenseTableKeys.userReferenceNumber]: false,
    [licenseTableKeys.requestNumber]: true,
    [licenseTableKeys.approvalReference]: true,
    [licenseTableKeys.approvalDate]: true,
    [licenseTableKeys.companyCode]: true,
    [licenseTableKeys.lastOperationDate]: true,
    [licenseTableKeys.requestDate]: false,
  },
};

export const getLicenseTableColumns = (
  tableName: string,
  licenseTypeTitle: string
): Record<string, ITableColumn<ILicenseSearchResult>> => ({
  [licenseTableKeys.status]: {
    key: licenseTableKeys.status,
    name: 'Status',
    sortable: true,
    colRenderer: (status, _, { ogaStatus, ogaStatusInNationalLang }) => (
      <LicenseStatus status={status} ogaStatus={ogaStatus} ogaStatusInNationalLang={ogaStatusInNationalLang} />
    ),
    className: styles.cell,
  },
  [licenseTableKeys.licenseType]: {
    key: licenseTableKeys.licenseType,
    name: licenseTypeTitle,
    sortable: true,
    className: styles.licenseTypeCell,
  },
  [licenseTableKeys.userReferenceNumber]: {
    key: licenseTableKeys.userReferenceNumber,
    name: 'User Ref. Number',
    sortable: true,
    className: styles.cell,
  },
  [licenseTableKeys.requestNumber]: {
    key: licenseTableKeys.requestNumber,
    name: 'Request Number',
    sortable: true,
    className: styles.cell,
  },
  [licenseTableKeys.requestDate]: {
    key: licenseTableKeys.requestDate,
    name: 'Request Date',
    sortable: true,
    colRenderer: (requestDate: string) => formatDate(requestDate),
    className: styles.cell,
  },
  ...(tableName.includes(LPCO_TABLE_NAME.LIST) && {
    [licenseTableKeys.approvalReference]: {
      key: licenseTableKeys.approvalReference,
      name: 'Approval Ref.',
      sortable: true,
      className: styles.cell,
    },
    [licenseTableKeys.approvalDate]: {
      key: licenseTableKeys.approvalDate,
      name: 'Approval Date',
      sortable: true,
      colRenderer: (approvalDate: string) => formatDate(approvalDate),
      className: styles.cell,
    },
  }),

  [licenseTableKeys.companyCode]: {
    key: licenseTableKeys.companyCode,
    name: 'Company',
    sortable: true,
    colRenderer: (companyCode, index, row) => <CompanyCell code={companyCode} description={row.companyDescription} />,
    className: styles.companyCell,
  },
  [licenseTableKeys.lastOperationDate]: {
    key: licenseTableKeys.lastOperationDate,
    name: 'Last Updated On',
    sortable: true,
    colRenderer: (lastOperationDate) => <DateTimeCell date={lastOperationDate} />,
    className: styles.cell,
  },
});

export const getLicenseTableColumnSettings = (
  t: (key: string, options?: { [key: string]: unknown }) => string,
  tableName: string,
  licenseAbbreviation: string
): IEachItem[] => {
  const savedSettings = getColumnSettings(tableName);
  const tableColumns = getLicenseTableColumns(
    tableName,
    t('Type of {{licenseAbbreviation}}', { licenseAbbreviation: t(licenseAbbreviation) })
  );

  const table = tableName.includes(LPCO_TABLE_NAME.LIST) ? LPCO_TABLE_NAME.LIST : LPCO_TABLE_NAME.NEW_REQUESTS;

  const tableColumnsArray = Object.values(tableColumns);

  const columnsAreChanged =
    savedSettings &&
    (tableColumnsArray.length !== savedSettings?.length || savedSettings.some((item) => !tableColumns[item.id]));

  if (columnsAreChanged) {
    localStorageService.remove(tableName);
  }

  if (savedSettings && !columnsAreChanged) {
    return savedSettings.map((item) => ({ ...item, title: tableColumns[item.id]?.name ?? item.title }));
  }

  return tableColumnsArray.map((column) => ({
    id: column.key,
    title: column.name,
    toggleProps: {
      checked: defaultCheckStatuses[table][column.key],
    },
  }));
};
